import type { CenterProps, IntrinsicSystemProps } from '@hover/blueprint';
import {
  Body,
  Center,
  forwardRef,
  motion,
  system,
  transition,
  useTheme,
} from '@hover/blueprint';
import { resetFocus } from '@hover/blueprint/foundation';
import { MotionConfig } from 'framer-motion';
import React, { useEffect, useState } from 'react';
import { matchPath, useLocation } from 'react-router';

import { useIsMobileSize } from 'src/modules/renderings/hooks';
import { TranslationNamespace, useTranslate } from 'src/shared/hooks';

import { fullscreenRoutes } from '../../constants';
import type { ProjectNavName } from '../Projects/Project/ProjectDetails';

import type { NavBarName, NavItemSchema } from './Navbar';

const Dot = forwardRef<IntrinsicSystemProps<'div'>, 'div'>((props, ref) => (
  <system.div
    backgroundColor="currentColor"
    borderRadius="round"
    height="6px"
    sx={transition.easing250('opacity', 'color')}
    width="6px"
    {...props}
    ref={ref}
  />
));

const MotionDot = motion(Dot);

interface NavbarButtonProps extends CenterProps {
  navbarItem: NavItemSchema;
  onClick: () => void;
  mode?: 'light' | 'dark';
  isSelected?: boolean;
  capitalize?: boolean;
}

const NavbarButton = ({
  navbarItem,
  isSelected = false,
  onClick,
  sx,
  capitalize = true,
  ...props
}: NavbarButtonProps) => {
  const t = useTranslate(TranslationNamespace.HOMEOWNER_APP);
  const isMobileSize = useIsMobileSize();

  const { name } = navbarItem;

  const translatedNavbarItemName = t(
    `navbar.${name as NavBarName | ProjectNavName}`,
  );

  const { pathname } = useLocation();
  const [previousPath, setPreviousPath] = useState(pathname);

  useEffect(() => {
    if (pathname !== previousPath) setPreviousPath(pathname);
  }, [pathname, previousPath]);

  const isFullscreenRoute = !!matchPath(previousPath, fullscreenRoutes);
  const {
    motion: { framer: spring },
  } = useTheme();

  return (
    <Center
      _focusVisible={{ textDecorationColor: 'darkAlpha.500' }}
      alignItems="center"
      aria-current={isSelected ? 'page' : undefined}
      aria-label={translatedNavbarItemName}
      as="a"
      bg="transparent"
      border="none"
      color="inherit"
      cursor="pointer"
      data-group
      flexDirection="column"
      href={navbarItem.path}
      justifyContent="center"
      onClick={event => {
        event.preventDefault();
        onClick?.();
      }}
      sx={{ ...sx, ...resetFocus() }}
      textDecoration="underline"
      textDecorationColor="transparent"
      {...props}
    >
      <Body
        as="span"
        color="inherit"
        isSemiBold={!isMobileSize}
        my="100"
        opacity={isSelected ? 1 : 0.8}
        size={300}
        textTransform={capitalize ? 'capitalize' : 'unset'}
        {...transition.easing250('color')}
      >
        {translatedNavbarItemName}
      </Body>
      <MotionConfig
        // Disable dot layout animation when we're returning from a fullscreen
        // route, otherwise it animates in a few cases when the fullscreen
        // route matches a different navigation item
        transition={isFullscreenRoute ? { duration: 0 } : spring.firm}
      >
        {isSelected ? (
          <MotionDot initial={false} layoutId="dot" />
        ) : (
          <Dot visibility="hidden" />
        )}
      </MotionConfig>
    </Center>
  );
};

export { NavbarButton };
